import role22MfrsSchema from '../../../../assets/forms/role-730000-mfrs.form.json';
import role22MpersSchema from '../../../../assets/forms/role-730000-mpers.form.json';
import { MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';

/**
 * Shared business rules for Role 22 (Notes - List of Notes).
 *
 * Role 22 is another accounting-standard-dependent sheet (mirrors the role 20
 * pattern): the logical sheet ships as two sibling form JSONs
 * (role-730000-mfrs.form.json — 138 TextBlock disclosures, and
 * role-730000-mpers.form.json — 83 TextBlock disclosures) that differ in both
 * the qname prefix (ifrs-full:* / ssmt-mfrs:* vs ssmt-mpers:*) AND the set of
 * controls they declare. The component picks between them at mount time based
 * on the filing's accounting standard; the pure validator has to make the
 * same selection from SheetValidationContext.accountingStandard.
 *
 * As of this writing neither schema carries `required`, `validators`, or
 * `requiredIfCrossSheet` metadata (every disclosure is an optional TextBlock),
 * so the validator currently emits no errors — but it is still built
 * schema-driven so any future schema revision that marks a disclosure
 * required (or introduces a cross-sheet gate) flows straight through without
 * a code change.
 *
 * Both role22-730000-notes-list-of-notes.component.ts AND
 * role22NotesListOfNotesValidator import from this file so the selection
 * literal lives in exactly one place. If the backend ever renames the
 * accounting-standard enum (e.g. MPERS is renamed), only this file changes.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MPERS schema. Any other value (including undefined,
 * '', or 'Malaysian Financial Reporting Standards') falls back to MFRS,
 * matching the component's `let schemaFile = 'role-730000-mfrs.form.json'`
 * default assignment.
 */
export const ROLE22_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE22_MFRS_SCHEMA_FILE = 'role-730000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE22_MPERS_SCHEMA_FILE = 'role-730000-mpers.form.json';

/**
 * Narrow shape of a role 22 schema control used by the pure validator. The
 * schemas carry richer metadata (ui, label, description, etc.) but the
 * validator only needs the validation-relevant fields — keeping this type
 * tight makes the validator resilient to unrelated schema additions.
 */
export interface Role22SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string }>;
  requiredIfCrossSheet?: {
    sheet: string;
    field: string;
    equals: string;
  };
}

export interface Role22Schema {
  role: string;
  sheetId: string;
  label: string;
  controls: Role22SchemaControl[];
}

/**
 * Resolve the correct role 22 schema filename for the given accounting
 * standard. Kept as a separate helper so the component's async fetch() and
 * the validator's static JSON import both derive their target from the same
 * selection rule.
 */
export function pickRole22SchemaFile(accountingStandard: string | undefined | null): string {
  if (accountingStandard === ROLE22_MPERS_ACCOUNTING_STANDARD) {
    return ROLE22_MPERS_SCHEMA_FILE;
  }
  return ROLE22_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 22 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-
 * time (the listing/dashboard's cache-miss path runs synchronously) so
 * both variants are imported eagerly and selected from memory.
 */
export function pickRole22Schema(
  accountingStandard: string | undefined | null
): Role22Schema {
  if (accountingStandard === ROLE22_MPERS_ACCOUNTING_STANDARD) {
    return role22MpersSchema as unknown as Role22Schema;
  }
  return role22MfrsSchema as unknown as Role22Schema;
}
